/**
 * Pipe — a grid-aligned chain of segments from a spout to a receptacle.
 *
 * Segment data model:
 *   Each segment has `{col, row, shape, enteringDir}`. `enteringDir` is the
 *   direction the pipe was going when it first entered this cell — used to
 *   choose initial shape (straight-h vs straight-v) and to detect direction
 *   changes for elbow promotion. World coords are derived only at render time
 *   via `gridToWorld()`.
 *
 * Shape derivation:
 *   • A fresh segment starts as a straight (h/v) based on `enteringDir`.
 *   • When the next segment is appended with a different `enteringDir`, the
 *     PREVIOUS segment gets its shape upgraded to the matching elbow piece
 *     (e.g. ES, WN). The rotation of the elbow sprite is determined entirely
 *     by `shape` — see `Pipe.angleFor`.
 *
 * State machine:
 *   building → connected → flowing
 *                       → burst
 *   • `building`: still under the drag pointer.
 *   • `connected`: pipe ends at a valid receptacle (or has been finalized via
 *     `reverseDirection` after a receptacle-started drag).
 *   • `flowing`: textures swap to the flowing variant; FlowFx animates molecules.
 *   • `burst`: a delayed transition (mismatched pipe whose molecules just
 *     reached the wrong receptacle). Pipe stays dimmed; Connector draws an X
 *     and a spurt.
 *
 * Direct-link special case:
 *   `directLink = true` pipes have one segment, render nothing, and exist only
 *   as logical entries so Connector treats organ-to-organ joints like any other
 *   spout→receptacle connection. The visible joint is the two castellated port
 *   pieces overlapping at the shared cell.
 *
 * Re-render triggers:
 *   `renderAllSegments()` is called after any segment-list change (extend, pop,
 *   reverse, manhattan re-route) and after state changes that affect texture
 *   choice (`setFlowing`, `markAsDirectLink`).
 */

import Phaser from 'phaser';
import { gridToWorld } from '../grid';
import type { Cell, Direction, FluidClass, PipeState, SegmentShape, SegmentSpec } from '../types';
import { cellEquals, cellAreAdjacent } from '../grid';
import { directionFromTo, elbowFor, opposite, straightFor } from '../types';

let nextPipeId = 1;

export class Pipe {
  readonly id: string;
  readonly scene: Phaser.Scene;
  fromPortId: string;
  fluidClass: FluidClass;
  toPortId: string | null = null;
  segments: SegmentSpec[] = [];
  state: PipeState = 'building';
  /** True if this pipe is a virtual organ-to-organ direct link (no draggable segments). */
  directLink: boolean = false;
  /**
   * Non-standard pipe variants:
   * - 'trachea': both endpoints are `bidirectional` ports. Segments stay
   *   gray (no fluidClass tint) even when flowing. A `TracheaFlowFx`
   *   manages the alternating single-sprite cycle instead of FlowFx.
   */
  specialKind: 'trachea' | null = null;

  readonly container: Phaser.GameObjects.Container;
  private segmentSprites: Phaser.GameObjects.Image[] = [];

  constructor(
    scene: Phaser.Scene,
    fromPortId: string,
    startCell: Cell,
    startEnteringDir: Direction,
    fluidClass: FluidClass = 'blood',
  ) {
    this.id = `pipe-${nextPipeId++}`;
    this.scene = scene;
    this.fromPortId = fromPortId;
    this.fluidClass = fluidClass;
    this.container = scene.add.container(0, 0);
    this.container.setDepth(5); // below ports (20) so the cone visually covers the pipe end
    this.appendStartSegment(startCell, startEnteringDir);
  }

  private appendStartSegment(cell: Cell, enteringDir: Direction): void {
    this.segments.push({
      col: cell.col,
      row: cell.row,
      shape: straightFor(enteringDir),
      enteringDir,
    });
    this.renderAllSegments();
  }

  /** Try to extend the pipe to a new adjacent cell. Returns true if a segment was appended. */
  tryExtendTo(cell: Cell): boolean {
    if (this.state !== 'building') return false;
    const last = this.segments[this.segments.length - 1];
    if (!last) return false;
    if (cellEquals({ col: last.col, row: last.row }, cell)) return false;
    if (!cellAreAdjacent({ col: last.col, row: last.row }, cell)) return false;
    if (this.containsCell(cell)) return false; // no self-cross

    const enteringDir = directionFromTo({ col: last.col, row: last.row }, cell)!;
    // If direction changes at `last`, update last's shape from straight to elbow.
    if (last.enteringDir !== enteringDir) {
      const elbowShape = elbowFor(last.enteringDir, enteringDir);
      if (elbowShape) last.shape = elbowShape;
    }
    this.segments.push({
      col: cell.col,
      row: cell.row,
      shape: straightFor(enteringDir),
      enteringDir,
    });
    this.renderAllSegments();
    return true;
  }

  /** Remove the most recent segment if the student backtracks. */
  popLastSegment(): boolean {
    if (this.segments.length <= 1) return false;
    this.segments.pop();
    // Recompute the shape of the new tail (it might have been an elbow that should revert to straight)
    if (this.segments.length >= 2) {
      const tail = this.segments[this.segments.length - 1];
      const prev = this.segments[this.segments.length - 2];
      const dirToTail = directionFromTo(
        { col: prev.col, row: prev.row },
        { col: tail.col, row: tail.row },
      )!;
      tail.shape = straightFor(dirToTail);
    } else {
      const only = this.segments[0];
      only.shape = straightFor(only.enteringDir);
    }
    this.renderAllSegments();
    return true;
  }

  containsCell(cell: Cell): boolean {
    return this.segments.some((s) => s.col === cell.col && s.row === cell.row);
  }

  lastCell(): Cell {
    const s = this.segments[this.segments.length - 1];
    return { col: s.col, row: s.row };
  }

  /** Mark this pipe as connected to a receptacle. Doesn't change visuals. */
  connectTo(receptacleId: string): void {
    this.toPortId = receptacleId;
    this.state = 'connected';
  }

  /**
   * Used when the pipe was built starting from a receptacle (bidirectional drag).
   * Reverses the segment list and swaps from/to so the data model is always
   * spout → receptacle. The physical pipe shape is unchanged — only enteringDir
   * (a per-segment field used for shape recomputation) is recalculated.
   */
  reverseDirection(spoutId: string, spoutFacing: Direction, spoutFluidClass: FluidClass): void {
    const oldFromPortId = this.fromPortId;
    this.segments.reverse();
    for (let i = 0; i < this.segments.length; i++) {
      const seg = this.segments[i];
      if (i === 0) {
        seg.enteringDir = spoutFacing;
      } else {
        const prev = this.segments[i - 1];
        seg.enteringDir = directionFromTo(
          { col: prev.col, row: prev.row },
          { col: seg.col, row: seg.row },
        )!;
      }
    }
    this.fromPortId = spoutId;
    this.toPortId = oldFromPortId;
    this.state = 'connected';
    this.fluidClass = spoutFluidClass;
    this.renderAllSegments();
  }

  /** Mark as a direct organ-to-organ link and re-render with the connector sprite. */
  markAsDirectLink(): void {
    this.directLink = true;
    this.renderAllSegments();
  }

  /** Replace the segment list (used when an organ moves and we re-route the pipe). */
  rebuildSegments(newSegments: SegmentSpec[]): void {
    this.segments = newSegments;
    this.renderAllSegments();
  }

  /** Update sprite textures based on the current state. */
  setFlowing(flowing: boolean): void {
    this.state = flowing ? 'flowing' : 'connected';
    this.renderAllSegments();
  }

  /** Render every segment as a sprite. Cheap enough for v1 (small pipe counts). */
  private renderAllSegments(): void {
    this.segmentSprites.forEach((s) => s.destroy());
    this.segmentSprites = [];

    // Direct-link pipes have no visible segments — the visual IS the two castellated
    // port pieces interlocking at the shared cell. Pipe is tracked logically only.
    if (this.directLink) return;

    const flowing = this.state === 'flowing';
    for (const seg of this.segments) {
      const world = gridToWorld({ col: seg.col, row: seg.row });
      const sprite = this.scene.add.image(world.x, world.y, this.textureFor(seg.shape, flowing));
      sprite.setAngle(this.angleFor(seg.shape));
      this.container.add(sprite);
      this.segmentSprites.push(sprite);
    }
  }

  private textureFor(shape: SegmentShape, flowing: boolean): string {
    const isStraight = shape === 'straight-h' || shape === 'straight-v';
    // Trachea pipes stay idle-gray regardless of state — gas exchange is
    // visualized via the single sprites traveling, not via pipe tint.
    if (!flowing || this.specialKind === 'trachea') {
      return isStraight ? 'pipe-straight-idle' : 'pipe-elbow-idle';
    }
    const suffix = this.fluidClass === 'gruel' ? '-flow-gruel' : '-flow';
    return isStraight ? `pipe-straight${suffix}` : `pipe-elbow${suffix}`;
  }

  private angleFor(shape: SegmentShape): number {
    switch (shape) {
      case 'straight-h':
        return 0;
      case 'straight-v':
        return 90;
      case 'elbow-ES':
        return 0; // base elbow: opens E + S
      case 'elbow-WS':
        return 90; // rotate 90° CW: E→S, S→W
      case 'elbow-WN':
        return 180;
      case 'elbow-EN':
        return 270;
    }
  }

  /** Return world-space points (segment centers) for the molecule path, in flow order. */
  pathPoints(): { x: number; y: number }[] {
    return this.segments.map((s) => gridToWorld({ col: s.col, row: s.row }));
  }

  /** Total pixel length along the molecule path (sum of leg lengths). */
  pathLengthPx(): number {
    const pts = this.pathPoints();
    let total = 0;
    for (let i = 0; i < pts.length - 1; i++) {
      total += Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
    }
    return total;
  }

  /** Fade pipe segments to simulate "broken." */
  dim(alpha: number): void {
    this.segmentSprites.forEach((s) => s.setAlpha(alpha));
  }

  /** Mark this pipe as burst at the joint nearest to a mismatched receptacle (last segment). */
  setBurst(): void {
    this.state = 'burst';
    // For v1 we don't change pipe color; the X sprite is rendered separately by the Connector.
  }

  destroyPipe(): void {
    this.segmentSprites.forEach((s) => s.destroy());
    this.segmentSprites = [];
    this.container.destroy();
  }

  // Re-export helpers used externally (so other modules don't depend on `types`):
  static opposite = opposite;
}
